import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { SystemService } from '../services/system.service';
import { tap, map, switchMap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  
  private employeesSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private matchingJobsSubject: BehaviorSubject<{ [employeeId: number]: any[] }> = new BehaviorSubject<{ [employeeId: number]: any[] }>({});

  private employeesDropdown: any = [];
  // Cache with timestamps to track expiration
  private cache = new Map<string, { data: any, expiration: number }>();
  private cacheExpirationTime = 10 * 60 * 1000; // 10 minutes

  constructor(private http: HttpClient, private system: SystemService) { }

  get employees$(): Observable<any[]> {
    return this.employeesSubject.asObservable();
  }

  get jobs$(): Observable<{ [employeeId: number]: any[] }> {
    return this.matchingJobsSubject.asObservable();
  }

  private isCacheValid(cacheKey: string): boolean {
    const cached = this.cache.get(cacheKey);
    return cached ? (Date.now() - cached.expiration) < this.cacheExpirationTime : false;
  }

  // Load employees with optional filter data
  loadEmployees(filterData?: any): void {
    let params = new HttpParams();
    if (filterData) {
      Object.keys(filterData).forEach(key => {
        if (filterData[key] !== null && filterData[key] !== undefined && filterData[key] !== '') {
          params = params.set(key, filterData[key]);
        }
      });
    }

    const cacheKey = `employees-${JSON.stringify(filterData)}`;
    if (this.isCacheValid(cacheKey)) {
      this.employeesSubject.next(this.cache.get(cacheKey)!.data);
    } else {
      this.http.get<any[]>(this.system.apiURL + '/employees', { params }).subscribe(employees => {
        this.employeesSubject.next(employees);
        this.cache.set(cacheKey, { data: employees, expiration: Date.now() });
      });
    }
  }

  // Load employees with optional filter data
  loadEmployeesDropdown(): Observable<any[]> {

    const cacheKey = `employees-dropdown`;
    
    if (this.employeesDropdown[cacheKey]) {
      return of(this.employeesDropdown[cacheKey]);
    } else {
      return this.http.get<any[]>(this.system.apiURL + '/employees-dropdown').pipe(
        tap(data => {
          this.employeesDropdown[cacheKey] = data; // Cache the data
        }),
      );
    }
  }

  getEmployeesBySearch(query: string): Observable<any[]> {
    // Check if the data is already in the cache
    if (this.cache[query]) {
      return of(this.cache[query]); // Return cached data
    }

    // Make an HTTP GET request if the data is not cached
    return this.http.get<any[]>(`${this.system.apiURL}/employees/search?query=${query}`).pipe(
      tap((data) => {
        this.cache['employeeSearch_' + query] = data; // Cache the fetched data
      }),
      catchError((error) => {
        console.error('Error fetching employees:', error);
        return of([]); // Return an empty array in case of error
      })
    );
  }



  // Get employee by ID (first try local, then fallback to server if not found)
  getEmployeeById(id: number, force: boolean = false): Observable<any> {
    const cacheKey = `employee-${id}`;
    if (this.isCacheValid(cacheKey) && !force) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.loadEmployeeFromServer(id);
  }

  // Load employee from server and add to local cache
  private loadEmployeeFromServer(id: number): Observable<any> {
    return this.http.get<any>(`${this.system.apiURL}/employees/${id}`).pipe(
      tap(employee => {
        const updatedEmployees = [...this.employeesSubject.value, employee];
        this.employeesSubject.next(updatedEmployees);
        this.cache.set(`employee-${id}`, { data: employee, expiration: Date.now() });
      })
    );
  }

  // Create new employee and add to local cache
  createEmployee(employee: any): Observable<any> {
    return this.http.post<any>(this.system.apiURL + '/employees', employee).pipe(
      tap(newEmployee => {
        this.employeesSubject.next([...this.employeesSubject.value, newEmployee]);
        this.cache.clear(); // Clear cache as the data has changed
      })
    );
  }

  // Update existing employee and modify in local cache
  updateEmployee(id: number, employee: any): Observable<any> {
    return this.http.put<any>(`${this.system.apiURL}/employees/${id}`, employee).pipe(
      tap(updatedEmployee => {
        const updatedEmployees = this.employeesSubject.value.map(e => (e.id === id ? updatedEmployee : e));
        this.employeesSubject.next(updatedEmployees);
        this.cache.set(`employee-${id}`, { data: updatedEmployee, expiration: Date.now() });
      })
    );
  }

  // Delete employee and remove from local cache
  deleteEmployee(id: number): Observable<any> {
    return this.http.delete<any>(`${this.system.apiURL}/employees/${id}`).pipe(
      tap(() => {
        const updatedEmployees = this.employeesSubject.value.filter(e => e.id !== id);
        this.employeesSubject.next(updatedEmployees);
        this.cache.delete(`employee-${id}`);
      })
    );
  }

  // Get organization structure
  getOrganizationStructure(departmentId: any = null, employeeId: any = null): Observable<any[]> {
    const params: any = {};
    if (departmentId) params.department_id = departmentId;
    if (employeeId) params.employee_id = employeeId;

    const cacheKey = `org-structure-${departmentId}-${employeeId}`;
    if (this.isCacheValid(cacheKey)) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.http.get<any[]>(this.system.apiURL + '/employees/organization-structure', { params }).pipe(
      tap(orgStructure => {
        this.cache.set(cacheKey, { data: orgStructure, expiration: Date.now() });
      })
    );
  }

  // Get employee objectives
  getEmployeeObjectives(): Observable<any[]> {
    const cacheKey = 'employee-objectives';
    if (this.isCacheValid(cacheKey)) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.http.get<any[]>(this.system.apiURL + '/employees/objectives').pipe(
      tap(objectives => {
        this.cache.set(cacheKey, { data: objectives, expiration: Date.now() });
      })
    );
  }

  // Get succession plan for an employee
  getSuccessionPlan(employeeId: number): Observable<any> {
    const cacheKey = `succession-${employeeId}`;
    if (this.isCacheValid(cacheKey)) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.http.get<any>(`${this.system.apiURL}/employees/${employeeId}/succession`).pipe(
      tap(successionPlan => {
        this.cache.set(cacheKey, { data: successionPlan, expiration: Date.now() });
      })
    );
  }

  // Get matching jobs for an employee and save them in the subject
  getEmployeeWithMatchingJobs(employeeId: number): Observable<any[]> {
    const cacheKey = `matching-jobs-${employeeId}`;
    if (this.isCacheValid(cacheKey)) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.http.get<any[]>(`${this.system.apiURL}/employees/${employeeId}/matching-jobs`).pipe(
      tap(matchingJobs => {
        const currentMatchingJobs = this.matchingJobsSubject.value;
        const updatedMatchingJobs = { ...currentMatchingJobs, [employeeId]: matchingJobs };
        this.matchingJobsSubject.next(updatedMatchingJobs);
        this.cache.set(cacheKey, { data: matchingJobs, expiration: Date.now() });
      })
    );
  }

  // Get subordinates
  getSubordinates(): Observable<any[]> {
    const cacheKey = 'subordinates';
    if (this.isCacheValid(cacheKey)) {
      return of(this.cache.get(cacheKey)!.data);
    }

    return this.http.get<any[]>(`${this.system.apiURL}/employees/subordinates`).pipe(
      tap(subordinates => {
        this.cache.set(cacheKey, { data: subordinates, expiration: Date.now() });
      })
    );
  }

  // Clear cache
  clearCache(): void {
    this.cache.clear();
  }

  saveAssignedModules(employeeId: number, modules: any[]): Observable<any> {
    const url = `${this.system.apiURL}/employees/${employeeId}/update-modules`;
    return this.http.post(url, { modules }).pipe(
      tap(response => console.log('Modules saved successfully:', response)),
      catchError(error => {
        console.error('Error saving modules:', error);
        return of(null);
      })
    );
  }
  
  getAssignedModules(employeeId: number, accessType: string): Observable<any[]> {
    const url = `${this.system.apiURL}/employees/${employeeId}/modules`;
    return this.http.get<any[]>(url, { params: { accessType } }).pipe(
      tap(modules => console.log('Fetched modules:', modules)),
      catchError(error => {
        console.error('Error fetching modules:', error);
        return of([]);
      })
    );
  } 
  
  
  createPerspective(employeeId: number, perspective: any): Observable<any> {
    return this.http.post<any>(`${this.system.apiURL}/employees/${employeeId}/perspective`, perspective);
  }

  getSuggestedSuccessors(employeeId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.system.apiURL}/employees/${employeeId}/suggested-successors`);
  }

  assignSuccessor(employeeId: number, successorId: number): Observable<any> {
    return this.http.post(`${this.system.apiURL}/employees/${employeeId}/assign-successor`, { successor_id: successorId });
  }
}
